import { Button, ButtonMode, ButtonSize, Checkbox, DateRange, Filter, Icon, ICONS, Loader, Tooltip, useGlobalModalContext } from '@creditsights/styleguide-web';
import { isNumber, remove } from 'lodash';
import numeral from 'numeral';
import React, { ReactElement, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import CountryFlag from '../../../components/country-flag/CountryFlag';
import { MODAL_TYPES } from '../../../components/modal/GlobalModal';
// eslint-disable-next-line import/no-named-as-default
import NoAccessLock from '../../../components/no-access/NoAccessLock';
import { COVENANT_COMPARE_DETAILS_ROUTE, NO_DATA } from '../../../Constants';
import useFetchCovenantCompareData from '../../../hooks/covenant-compare/useFetchCovenantCompareData';
import useFetchCovenantCompareFilters from '../../../hooks/covenant-compare/useFetchCovenantCompareFilters';
import { AccessStatus, CovenantComparison } from '../../../models/Securities';
import { formatDate } from '../../../utils/date.utils';
import { formatResultsText, hasMoreData } from '../../../utils/page.utils';
import { createTagMap } from '../../../utils/tag.utils';

import '../Compare.scss';
import '../../../scss/filter-tsx.scss';

const MAX_SELECTION = 75;

function CovenantCompare(): ReactElement {
  const PAGE_SIZE = 100;
  const INITIAL_DATE_RANGE = { startDate: null, endDate: null, relativeRange: null };
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [filterConfig, setFilterConfig] = useState<any>({});
  const [sectorMap, setSectorMap] = useState<any>();
  const [countryMap, setCountryMap] = useState<any>();
  const { data: filters, isLoading: filtersLoading } = useFetchCovenantCompareFilters();
  const [sort, setSort] = useState({ field: 'issuer', asc: true });
  const [issueDateFilter, setIssueDateFilter] = useState<any>(INITIAL_DATE_RANGE);
  const [maturityDateFilter, setMaturityDateFilter] = useState<any>(INITIAL_DATE_RANGE);
  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useFetchCovenantCompareData(sort, issueDateFilter, maturityDateFilter, filterConfig, PAGE_SIZE);
  const [selections, setSelections] = useState<any[]>([]);
  const { showModal } = useGlobalModalContext();

  useEffect(() => {
    if (search) {
      try {
        const config = JSON.parse(decodeURI(search.substring(1)));
        setSort(config.sort);
        setFilterConfig(config.filterConfig);
        setIssueDateFilter(config.issueDateFilter);
        setMaturityDateFilter(config.maturityDateFilter);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  useEffect(() => {
    if (filters) {
      setSectorMap(createTagMap(filters.sectors));
      setCountryMap(createTagMap(filters.country_code));
    }
  }, [filters]);

  useEffect(() => {
    setSelections([]);
    navigate(`${pathname}?${JSON.stringify({ sort, filterConfig, issueDateFilter, maturityDateFilter })}`, { replace: true });
  }, [sort, filterConfig, issueDateFilter, maturityDateFilter]);

  const resetFilters = () => {
    setFilterConfig({});
    setIssueDateFilter({ ...INITIAL_DATE_RANGE });
    setMaturityDateFilter({ ...INITIAL_DATE_RANGE });
  };

  const updateFilterConfig = (key, selected) => {
    setFilterConfig((config) => {
      // eslint-disable-next-line no-param-reassign
      config[key] = selected;

      if (selected.length === 0) {
        // eslint-disable-next-line no-param-reassign
        delete config[key];
      }

      return { ...config };
    });
  };

  const renderFilters = () => {
    return (
      <div className="filters-ctx">
        {Object.keys(filters).map((key) => (
          <Filter key={key} data={filters[key].data} placeholder={filters[key].name} checkedKeys={filterConfig[key] || []} onChange={(checked) => updateFilterConfig(key, checked)} />
        ))}
        <DateRange label="Issue Date" startDate={issueDateFilter.startDate} endDate={issueDateFilter.endDate} relativeRange={issueDateFilter.relativeRange} resetInput={0} error={false} setError={() => false} onChange={setIssueDateFilter} />
        <DateRange
          label="Maturity Date"
          startDate={maturityDateFilter.startDate}
          endDate={maturityDateFilter.endDate}
          relativeRange={maturityDateFilter.relativeRange}
          resetInput={0}
          error={false}
          setError={() => false}
          onChange={setMaturityDateFilter}
        />
        <div className="separator" />
        <button className="reset-button" onClick={resetFilters} type="button">
          <Icon name={ICONS.Clear} width={12} height={12} /> Reset
        </button>
      </div>
    );
  };

  const renderIssuerLink = (bond) => {
    if (bond.cs_universal_id) {
      return (
        <NavLink className="company-name" to={`/companies/${bond.cs_universal_id}`}>
          {bond.issuer}
        </NavLink>
      );
    }
    return <span>{bond.issuer}</span>;
  };

  const renderCountryFlag = (security) => {
    if (!security.country_code) {
      return NO_DATA;
    }

    return <CountryFlag countryName={countryMap[security.country_code]} iso3Code={security.country_code} />;
  };

  const renderPercentage = (value) => {
    if (value || value === 0) {
      const p: any = `${value}`.replace('%', '');

      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(p * 1) && isNumber(p * 1)) {
        return `${numeral(p * 1).format('0.000')}%`;
      }
    }

    return value;
  };

  const showSelectAll = () => {
    let size = 0;

    if (data) {
      for (const page of data.pages) {
        size += page.results.length;
      }
    }

    return size <= MAX_SELECTION;
  };

  const updateSort = (field) => {
    setSort((v) => {
      if (v.field === field) {
        return {
          field,
          asc: !v.asc,
        };
      }
      return {
        field,
        asc: true,
      };
    });
  };

  const selectAll = (checked) => {
    if (!checked) {
      setSelections([]);
    } else if (data) {
      const items: any = [];
      for (const page of data.pages) {
        for (const item of page.results) {
          if (!isLocked(item)) {
            items.push(item.id);
          }
        }
      }
      setSelections(items);
    }
  };

  const showDetails = () => {
    navigate(`${COVENANT_COMPARE_DETAILS_ROUTE}?${JSON.stringify(selections)}`);
  };

  const handleSelection = (checked, id) => {
    setSelections((array) => {
      if (checked) {
        array.push(id);
      } else {
        remove(array, (item) => item === id);
      }

      return [...array];
    });
  };

  const renderMultiTags = (tags, map) => {
    if (tags?.length > 0) {
      return tags.map((item) => <div key={item}>{map[item]}</div>);
    }

    return NO_DATA;
  };

  const handleNoAccessClick = () => {
    showModal(MODAL_TYPES.UPGRADE_SUBSCRIPTION);
  };

  const isLocked = (security: CovenantComparison) => {
    return security.access_status === AccessStatus.subscriptionRequired;
  };

  return (
    <div className="compare-page-list">
      <div className="header-ctx">
        <div className="header">
          <div className="title">Bond Document Compare Tool</div>
        </div>
      </div>
      <div className="covenants-ctx">
        {!filtersLoading && renderFilters()}
        <div className="table-ctx">
          <div className="section-title">
            {data?.pages[0]?.totalResults && (
              <>
                <div className="security-count">{formatResultsText(data)}</div>
                <Tooltip content={`Can compare up to ${MAX_SELECTION} deals at a time`} disabled={selections.length <= MAX_SELECTION}>
                  <div className="actions">
                    <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} onClick={showDetails} disabled={selections.length === 0 || selections.length > MAX_SELECTION}>
                      Compare Selected {selections.length > 0 ? `(${selections.length})` : ''}
                    </Button>
                  </div>
                </Tooltip>
              </>
            )}
          </div>

          {(isLoading || !sectorMap || !countryMap) && (
            <div className="loading-ctx">
              <Loader className="cs-loader" position="center" />
            </div>
          )}
          {!isLoading && sectorMap && countryMap && (
            <>
              <table className="ui table">
                <thead>
                  <th className="selection">{showSelectAll() && <Checkbox dataTestId="select-all" onChange={(e, data) => selectAll(data.checked)} />}</th>
                  <th className="sortable" style={{ width: 250 }} onClick={() => updateSort('issuer')}>
                    Issuer <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('issue_name')}>
                    Issue <i className="sort icon" />
                  </th>
                  <th className="sortable ui text right aligned" onClick={() => updateSort('interest_rate')}>
                    Interest Rate <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('interest_rate_type')}>
                    Interest Rate Type <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('issue_date')}>
                    Issue Date <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('maturity_date')}>
                    Maturity Date <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('use_of_proceeds')}>
                    UOP <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('industry')}>
                    Sector/Subsector <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('region')}>
                    Region <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('country_code')}>
                    Country <i className="sort icon" />
                  </th>
                </thead>
                <tbody>
                  {data &&
                    data.pages.map((page) =>
                      page.results.map((security) => (
                        <tr key={security.id} className={`table-rows ${isLocked(security) ? 'locked' : ''}`} onClick={isLocked(security) ? handleNoAccessClick : undefined}>
                          <td className="checkbox-container">
                            <Checkbox dataTestId={security.id} checked={selections.indexOf(security.id) !== -1} disabled={isLocked(security)} onChange={(e, data) => handleSelection(data.checked, security.id)} />
                          </td>
                          <td className="issuer">{renderIssuerLink(security)}</td>
                          <td>{security.issue_name}</td>
                          <td className="ui text right aligned">{renderPercentage(security.interest_rate)}</td>
                          <td>{security.interest_rate_type}</td>
                          <td>{formatDate(security.issue_date)}</td>
                          <td>{formatDate(security.maturity_date)}</td>
                          <td>{security.use_of_proceeds}</td>
                          <td>{renderMultiTags(security.sectors, sectorMap)}</td>
                          <td>{security.region}</td>
                          <td className="country">
                            {renderCountryFlag(security)}
                            {isLocked(security) && (
                              <div className="no-access-wrapper">
                                <NoAccessLock accessStatus={security.access_status} />
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                </tbody>
              </table>

              {hasMoreData(data, 100, data?.pages[0]?.totalResults) && (
                <div className="next-page">
                  <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} loading={isFetchingNextPage} disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
                    Load 100 more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CovenantCompare;
